import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { addAccount } from "../../api/index"; // API call
import { IoMdArrowBack } from "react-icons/io";
import { FiBriefcase, FiUser, FiHash, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

const BankDetailsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const decoded = jwtDecode(token);
  const userId = decoded.id || decoded._id;

  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAddAccount = async () => {
    setError("");
    setSuccess("");

    if (!bankName || !accountNumber || !accountHolder) {
      setError("⚠️ Please fill in all fields");
      return;
    }

    try {
      setLoading(true);
      const res = await addAccount({
        userId,
        bankName,
        accountNumber,
        accountHolder,
      });

      if (res.data?.message) {
        setSuccess("✅ Account added successfully");
        setBankName("");
        setAccountNumber("");
        setAccountHolder("");
        setTimeout(() => navigate(-1), 1500);
      } else {
        setError(res?.data?.error || "❌ Failed to add account");
      }
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || "❌ Something went wrong on server");
      } else if (err.request) {
        setError("❌ No response from server. Please try again.");
      } else {
        setError("❌ An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight pr-10">Bank Setup</h1>
        <div />
      </div>

      <div className="max-w-md mx-auto p-6 pt-10">
        {/* 🏦 Visual Identity Card */}
        <div className="bg-gray-900 p-8 rounded-[2.5rem] shadow-2xl shadow-gray-400 mb-10 text-white relative overflow-hidden">
          <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-orange-600/20 rounded-full blur-3xl"></div>
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-orange-500">
              <FiBriefcase size={24} strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-xl font-black">Payout Details</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Where you receive funds</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
          {/* Bank Name Input */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Bank Name</label>
            <div className="relative">
              <FiBriefcase className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 pl-12 pr-5 font-bold text-gray-900 transition-all outline-none"
                placeholder="e.g. Zenith Bank"
              />
            </div>
          </div>

          {/* Account Number Input */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Account Number</label>
            <div className="relative">
              <FiHash className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 pl-12 pr-5 font-bold text-gray-900 transition-all outline-none"
                placeholder="0000000000"
              />
            </div>
          </div>

          {/* Account Holder Input */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Account Holder Name</label>
            <div className="relative">
              <FiUser className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={accountHolder}
                onChange={(e) => setAccountHolder(e.target.value)}
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 pl-12 pr-5 font-bold text-gray-900 transition-all outline-none"
                placeholder="John Doe"
              />
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-2xl border border-red-100">
              <FiAlertCircle className="shrink-0" />
              <p className="text-[11px] font-black uppercase tracking-tight leading-tight">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-2xl border border-green-100">
              <FiCheckCircle className="shrink-0" />
              <p className="text-[11px] font-black uppercase tracking-tight leading-tight">{success}</p>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleAddAccount}
            disabled={loading}
            className={`w-full py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-sm transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 ${
              loading 
                ? "bg-gray-200 text-gray-400 cursor-not-allowed" 
                : "bg-orange-600 text-white hover:bg-orange-700 shadow-orange-200"
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
            ) : (
              "Save Account"
            )}
          </button>
        </div>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Storage • Used for Payouts • OurASAP
        </p>
      </div>
    </div>
  );
};

export default BankDetailsPage;